/**
 * Hooks para el módulo de Pasarelas de Pago.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

export interface PaymentGateway {
  id: string
  gateway_type: 'stripe' | 'flow' | 'payku'
  gateway_type_display: string
  is_active: boolean
  is_sandbox: boolean
  sandbox_toggle_locked: boolean
  sandbox_lock_reason: string
  has_credentials: boolean
  has_webhook_secret: boolean
  deposit_account_id: string | null
  deposit_account_display: string | null
  created_at: string
  updated_at: string
}

export interface PaymentLink {
  id: string
  document_id: string
  gateway_type: string
  external_id: string
  url: string
  amount: string
  currency: string
  status: 'active' | 'paid' | 'expired' | 'cancelled'
  expires_at: string | null
  paid_at: string | null
  created_at: string
}

export interface CustomerPaymentToken {
  id: string
  customer_id: string
  gateway_type: string
  card_brand: string
  card_last4: string
  card_exp_month: number | null
  card_exp_year: number | null
  is_default: boolean
  created_at: string
}

// ─── Gateway hooks ────────────────────────────────────────────────────────────

export function usePaymentGateways() {
  return useQuery<PaymentGateway[]>({
    queryKey: ['payment-gateways'],
    queryFn: () => apiClient.get('/payments/gateways/').then(r => r.data),
    staleTime: 60_000,
  })
}

export function useUpdateGateway() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      gateway_type: string
      is_active?: boolean
      is_sandbox?: boolean
      credentials?: Record<string, string>
      webhook_secret?: string
      deposit_account_id?: string | null
    }) => apiClient.post('/payments/gateways/', data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-gateways'] })
    },
  })
}

export function useTestGateway() {
  return useMutation({
    mutationFn: (gateway_type: string) =>
      apiClient.post(`/payments/gateways/${gateway_type}/test/`).then(r => r.data),
  })
}

// ─── Payment Link hooks ───────────────────────────────────────────────────────

export function useCreatePaymentLink() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { document_id: string; gateway_type: string }) =>
      apiClient.post('/payments/links/', data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-documents'] })
    },
  })
}

export function usePaymentLinkStatus(linkId: string | null) {
  return useQuery<PaymentLink>({
    queryKey: ['payment-link-status', linkId],
    queryFn: () => apiClient.get(`/payments/links/${linkId}/status/`).then(r => r.data),
    enabled: !!linkId,
    staleTime: 30_000,
  })
}

export function useCancelPaymentLink() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (linkId: string) =>
      apiClient.post(`/payments/links/${linkId}/cancel/`).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-gateways'] })
    },
  })
}

// ─── Token hooks ──────────────────────────────────────────────────────────────

export function useCustomerTokens(customerId?: string) {
  return useQuery<CustomerPaymentToken[]>({
    queryKey: ['payment-tokens', customerId],
    queryFn: () => {
      const params = customerId ? { customer_id: customerId } : {}
      return apiClient.get('/payments/tokens/', { params }).then(r => r.data)
    },
    staleTime: 60_000,
  })
}

export function useDeleteToken() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (tokenId: string) =>
      apiClient.delete(`/payments/tokens/${tokenId}/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-tokens'] })
    },
  })
}

export function useInitiateTokenRegistration() {
  return useMutation({
    mutationFn: (data: { gateway_type: string; customer_id: string }) =>
      apiClient.post('/payments/tokens/', data).then(r => r.data),
  })
}

export function useChargeWithToken() {
  return useMutation({
    mutationFn: (data: { token_id: string; document_id: string }) =>
      apiClient.post('/payments/charge/', data).then(r => r.data),
  })
}
